import React, { FC, memo } from 'react'
import cn from 'classnames'
import { useStore } from 'store'
import { observer } from 'mobx-react'
import ReactPlayer from 'react-player'

import { InnerContainer } from 'containers/InnerContainer'
import { Play } from 'components/Play'
import { ButtonMUI } from 'components/ButtonMUI'

import { useMediaQuery } from 'utils/hooks'
import { staticMultimedia } from 'utils'

import { ReactComponent as DualLens } from 'assets/images/dual_lens.svg'
import { ReactComponent as QLEDMiniLed } from 'assets/images/QLED_mini_led.svg'
import { ReactComponent as HorizontalVertical } from 'assets/images/horizontal_vertical.svg'
import { ReactComponent as Ultraleap } from 'assets/images/ultraleap.svg'
import { ReactComponent as MpxFov } from 'assets/images/mpx_fov.svg'
import { ReactComponent as Calibration } from 'assets/images/calibration.svg'
import { ReactComponent as ModularHardware } from 'assets/images/modular_hardware.svg'
import { ReactComponent as PrintableHardware } from 'assets/images/printable_hardware.svg'
import { ReactComponent as MadeIn } from 'assets/images/made_in.svg'


import styles from './styles.module.scss'

interface IProps {
}

enum FEATURE {
  DUAL_LENS,
  QLED_MINI_LED,
  HORIZONTAL_VERTICAL,
  ULTRALEAP,
  MPX_FOV,
  CALIBRATION
}

const getFeatureInfo = (type: FEATURE) => {
  switch (type) {
    case FEATURE.DUAL_LENS:
      return {
        icon: <DualLens className={styles.dualLensIcon}/>,
        description: 'Dual-lens aspheric \n' +
          'modules, 35 PPD \n' +
          'peak fidelity'
      }
    case FEATURE.QLED_MINI_LED:
      return {
        icon: <QLEDMiniLed className={styles.QLEDMiniLedIcon}/>,
        description: 'QLED Mini-Led Fast LCD\n' +
          '2× 2880 x 2880 pixels'
      }
    case FEATURE.HORIZONTAL_VERTICAL:
      return {
        icon: <HorizontalVertical className={styles.horizontalVerticalIcon}/>,
        description: 'Horizontal: 125°\n' +
          'Vertical: 100°'
      }
    case FEATURE.ULTRALEAP:
      return {
        icon: <Ultraleap className={styles.ultraleapIcon}/>,
        description: 'Ultraleap 170×170° FOV\n' +
          '120Hz refresh rate'
      }
    case FEATURE.MPX_FOV:
      return {
        icon: <MpxFov className={styles.mpxFovIcon}/>,
        description: '2× 12 Mpx 4056x3040\n' +
          'FOV 125° × 100°'
      }
    case FEATURE.CALIBRATION:
      return {
        icon: <Calibration className={styles.calibrationIcon}/>,
        description: '120Hz 1-point calibration\n' +
          'VR & XR FOV rendering'
      }
  }
}

const OpenSection: FC<IProps> = props => {
  const width_1024 = useMediaQuery('(max-width: 1024px)')

  const getFeatureComponent = (value: FEATURE) => {
    const feature = getFeatureInfo(value)

    return (
      <div className={styles.feature}>
        <div className={styles.featureIcon}>
          {feature.icon}
        </div>
        <div className={styles.featureDescription}>
          {feature.description}
        </div>
      </div>
    )
  }


  const price = <a href={'#reserveYourSpot'} className={styles.price}>
    <img src={staticMultimedia.startingAt} alt="price"/>
  </a>
  // f_auto,q_auto/
  //   .delivery(quality(auto()))
  return (
    <section className={styles.section}>
      <div className={styles.sectionInner}>
        <div className={styles.playerWrapper}>
          <div className={styles.mask}/>
          <ReactPlayer
            url={staticMultimedia.main_headset}
            className={styles.reactPlayer}
            volume={0}
            playing={true}
            loop
            muted
            playsinline
            width="100%"
            height="100%"
          />
        </div>
        <InnerContainer>
          <div className={styles.openContainer}>

            <div className={styles.VRInfoBlock}>
              <img className={styles.somniumVR}
                   src={staticMultimedia.somniumVR1}
                   alt="logoVR1"/>
              <div className={styles.titleBlock}>
                <div className={styles.title}>
                  Open, Modular, <span>Yours</span>
                </div>
                <div className={styles.subTitle}>Next Generation Clarity and FOV in VR & MR</div>
              </div>

              {width_1024 ? price : null}
              <div className={styles.buttonsBlock}>
                <a href={'#unveilTheSpectacle'}>
                  <ButtonMUI styleType="blue_2"
                             type={'button'}>
                    Learn More
                  </ButtonMUI>
                </a>
                <a href={'#reserveYourSpot'} className={styles.reserveWrapper}>
                  <ButtonMUI styleType="blue"
                             type={'button'}>
                    Reserve your spot
                  </ButtonMUI>
                  <div className={styles.playWrapper}>
                    <Play/>
                  </div>
                </a>
              </div>

              <div className={styles.featureBlock}>
                <div className={styles.featureBox}>
                  {getFeatureComponent(FEATURE.DUAL_LENS)}
                  {getFeatureComponent(FEATURE.QLED_MINI_LED)}
                </div>
                <div className={styles.featureBox}>
                  {getFeatureComponent(FEATURE.HORIZONTAL_VERTICAL)}
                  {getFeatureComponent(FEATURE.ULTRALEAP)}
                </div>
                <div className={styles.featureBox}>
                  {getFeatureComponent(FEATURE.MPX_FOV)}
                  {getFeatureComponent(FEATURE.CALIBRATION)}
                </div>

              </div>
            </div>

            <div className={styles.madeInBlock}>
              {!width_1024 ? price : null}
              <div className={styles.madeInBox}>
                <div className={styles.hardwareBox}>
                  <ModularHardware className={styles.modularHardwareIcon}/>
                  <div className={styles.hardwareDescription}>
                    {'Modular\n' + 'Hardware'}
                  </div>
                </div>
                <div className={styles.hardwareBox}>
                  <PrintableHardware className={styles.printableHardwareIcon}/>
                  <div className={styles.hardwareDescription}>
                    {'3D Printable\n' + 'Hardware'}
                  </div>
                </div>
                <MadeIn className={styles.madeInIcon}/>
              </div>
            </div>
          </div>
        </InnerContainer>
      </div>
    </section>
  )
}

export default memo(OpenSection)
